"use client";

import { memo, useEffect, useRef, useState } from "react";
import equal from "fast-deep-equal";
import { alertLog } from "@/lib/alert-display-log";

interface DashboardViewProps {
  html: string;
  noBorder?: boolean;
  isReplay?: boolean;
}

function DashboardViewComponent({ html, noBorder = false, isReplay = false }: DashboardViewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [iframeHeight, setIframeHeight] = useState(800);

  // Mount / html-change log: bytes of HTML, whether it carries the expected
  // marker, whether it references Plotly or window.CHART_DATA.
  useEffect(() => {
    const bytes = typeof html === "string" ? html.length : 0;
    const sample = typeof html === "string" ? html.slice(0, 400) : "";
    const hasMarker =
      typeof html === "string" &&
      html.includes('data-xynth-component="dashboard"');
    const hasPlotly =
      typeof html === "string" && html.toLowerCase().includes("plotly");
    const hasChartData =
      typeof html === "string" && html.includes("window.CHART_DATA");
    alertLog("dashboard:html_set", {
      bytes,
      isReplay,
      noBorder,
      hasMarker,
      hasPlotly,
      hasChartData,
      sample,
    });
  }, [html, isReplay, noBorder]);

  // Listen for height messages and evidence clicks from the iframe
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === "xynth-dashboard-height" && typeof e.data.height === "number") {
        setIframeHeight(e.data.height + 20); // small buffer
        alertLog("dashboard:height_report", { height: e.data.height });
      }
      if (e.data?.type === "xynth-evidence" && typeof e.data.exhibit === "string") {
        alertLog("dashboard:evidence_click", {
          exhibit: String(e.data.exhibit).slice(0, 120),
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!containerRef.current || typeof ResizeObserver === "undefined") return;

    const notifyDashboardResize = () => {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "xynth-dashboard-resize" },
        "*"
      );
    };

    let lastWidth = containerRef.current.getBoundingClientRect().width;
    const resizeObserver = new ResizeObserver((entries) => {
      const nextWidth = entries[0]?.contentRect.width ?? 0;
      if (Math.abs(nextWidth - lastWidth) < 1) return;
      lastWidth = nextWidth;
      notifyDashboardResize();
    });

    resizeObserver.observe(containerRef.current);
    const timeoutIds = [150, 500, 1000].map((delay) =>
      window.setTimeout(notifyDashboardResize, delay)
    );

    return () => {
      resizeObserver.disconnect();
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [html]);

  // Inject a height-reporting script into the dashboard HTML
  const enhancedHtml = html
    ? html.replace(
        "</body>",
        `<script>
          (function() {
            function reportHeight() {
              var h = document.body.scrollHeight || document.documentElement.scrollHeight;
              window.parent.postMessage({ type: "xynth-dashboard-height", height: h }, "*");
            }
            reportHeight();
            new MutationObserver(reportHeight).observe(document.body, { childList: true, subtree: true });
            window.addEventListener("resize", reportHeight);
            window.addEventListener("message", function(event) {
              if (!event || !event.data || event.data.type !== "xynth-dashboard-resize") return;
              window.dispatchEvent(new Event("resize"));
              reportHeight();
            });
            setTimeout(reportHeight, 500);
            setTimeout(reportHeight, 2000);
          })();
        </script></body>`
      )
    : html;

  const showSignalSummarySpotlight =
    typeof html === "string" &&
    /XRN\s+Passes\s+C-Suite\s+Conviction\s+Screen/i.test(html);

  return (
    <div
      ref={containerRef}
      className={
        noBorder
          ? "w-full max-w-full relative"
          : "w-full max-w-full rounded-lg overflow-hidden bg-card border border-border relative"
      }
    >
      {showSignalSummarySpotlight && (
        <div
          data-testid="dashboard-signal-summary-spotlight"
          className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[148px]"
        />
      )}
      <iframe
        ref={iframeRef}
        srcDoc={enhancedHtml}
        className="w-full"
        style={{
          width: "100%",
          height: `${iframeHeight}px`,
          border: "none",
          backgroundColor: "transparent",
          borderRadius: noBorder ? undefined : "0.5rem",
        }}
        title="Analysis Dashboard"
        sandbox="allow-scripts allow-same-origin"
      />
    </div>
  );
}

export const DashboardView = memo(
  DashboardViewComponent,
  (prevProps, nextProps) => {
    return (
      equal(prevProps.html, nextProps.html) &&
      prevProps.noBorder === nextProps.noBorder &&
      prevProps.isReplay === nextProps.isReplay
    );
  }
);
